import { useEffect, useState } from 'react';
import axios from 'axios';
import { tmAIAssistantDefaultValue } from '../constant';
import { FileText, Bot, User, X, UserRound, BookOpen, Trash2 } from 'lucide-react';
import Loader from '../components/Loader';
import Navbar from '../components/navbar';
import { useTheme } from '../context/ThemeContext';

export default function AdminDashboard() {
  const { theme, setTheme } = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [chatData, setChatData] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [persona, setPersona] = useState('');
  const [personaInformation, setPersonaInformation] = useState('');
  const [selectedUserMeta, setSelectedUserMeta] = useState(null);
  const [modalView, setModalView] = useState('transcript');
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);

  const token = localStorage.getItem('authToken');

  useEffect(() => {
    fetchUsers();
  }, []);

  // Prevent navigation away from admin dashboard via back button
  useEffect(() => {
    // Push a new history entry so there's something to go back to
    window.history.pushState({ isAdminDashboard: true }, '', window.location.href);

    const handlePopState = (e) => {
      // Immediately go forward to prevent leaving the page
      window.history.forward();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const applyThemeChoice = (nextTheme) => {
    document.documentElement.setAttribute('data-theme', nextTheme);
    document.body?.setAttribute('data-theme', nextTheme);
    localStorage.setItem('themeMode', 'manual');
    localStorage.setItem('theme', nextTheme);
    setTheme(nextTheme);
  };

  useEffect(() => {
    const handleShortcut = (e) => {
      const key = typeof e.key === 'string' ? e.key.toLowerCase() : '';
      if (key !== 'i' && e.code !== 'KeyI') return;
      e.preventDefault();
      applyThemeChoice(theme === 'dark' ? 'light' : 'dark');
    };

    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [theme]);

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${tmAIAssistantDefaultValue.tmAIChatbotApiUrl}history/admin/users`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setUsers(response?.data?.data || []);
    } catch (err) {
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchChatHistory = async (user, view = 'transcript') => {
    try {
      setChatLoading(true);
      setSelectedSession(user.email);
      setSelectedUserMeta(user);
      setModalView(view);

      const response = await axios.post(
        `${tmAIAssistantDefaultValue.tmAIChatbotApiUrl}history/chat/session`,
        {
          email: user.email,
          interview_no: user.interview_no,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setChatData(response?.data?.question_answers || []);

      setCourses(response?.data?.courses || []);
      setPersona(
        response?.data?.persona ||
          response?.data?.profile?.persona ||
          user?.persona ||
          'Not available'
      );
      setPersonaInformation(response?.data?.persona_information);
    } catch (err) {
      console.error('Failed to fetch chat history');
    } finally {
      setChatLoading(false);
    }
  };

  const deleteUser = async (user) => {
    const confirmed = window.confirm(`Delete user ${user.email}? This cannot be undone.`);
    if (!confirmed) return;

    const deleteKey = `${user.session_id}-${user.interview_no || 'na'}-${user.email || ''}`;
    setDeleteLoadingId(deleteKey);
    setError('');

    try {
      await axios.delete(
        `${tmAIAssistantDefaultValue.tmAIChatbotApiUrl}history/admin/delete-user`,
        {
          data: { email: user.email, interview_no: user.interview_no },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (selectedSession === user.email) {
        closeModal();
      }
      await fetchUsers();
    } catch (err) {
      setError('Failed to delete user');
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const closeModal = () => {
    setSelectedSession(null);
    setChatData([]);
    setCourses([]);
    setPersona('');
    setSelectedUserMeta(null);
    setModalView('transcript');
  };

  const getCourseLink = (course, linkType = 'video') => {
    const keys =
      linkType === 'video'
        ? [
            'video_url',
            'videoLink',
            'video',
            'youtube_url',
            'youtube',
            'resource_url',
            'link',
            'url',
          ]
        : ['pdf_url', 'pdfLink', 'pdf', 'document_url', 'resource_url', 'link', 'url'];

    return (
      keys
        .map((key) => course?.[key])
        .find((value) => typeof value === 'string' && value.startsWith('http')) || null
    );
  };

  const modalTitleByView = {
    transcript: `Transcript - ${selectedSession}`,
    persona: `Persona - ${selectedSession}`,
    courses: `Personalised learning pathways - ${selectedSession}`,
  };

  return (
    <>
      <Navbar />

      <main className="min-h-[calc(100vh-90px)] overflow-y-auto bg-panel px-3 sm:px-6 lg:px-8">
        <section className="max-w-7xl mx-auto">
          <header className="mb-8">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-text-light">Users</h1>
                <p className="mt-2 text-base text-text-muted">
                  Press <strong>I</strong> or use the buttons to switch light and dark mode.
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => applyThemeChoice('light')}
                  className={`rounded-lg border px-4 py-2 text-base font-semibold transition focus:outline-none focus:ring-2 focus:ring-accent ${
                    theme === 'light'
                      ? 'bg-accent text-[color:var(--accent-foreground)] border-accent shadow-md'
                      : 'bg-bg text-text-light border-glass-border hover:bg-muted'
                  }`}
                  aria-pressed={theme === 'light'}
                >
                  Light Mode
                </button>
                <button
                  type="button"
                  onClick={() => applyThemeChoice('dark')}
                  className={`rounded-lg border px-4 py-2 text-base font-semibold transition focus:outline-none focus:ring-2 focus:ring-accent ${
                    theme === 'dark'
                      ? 'bg-accent text-[color:var(--accent-foreground)] border-accent shadow-md'
                      : 'bg-bg text-text-light border-glass-border hover:bg-muted'
                  }`}
                  aria-pressed={theme === 'dark'}
                >
                  Dark Mode
                </button>
              </div>
            </div>
          </header>

          {loading && <Loader />}
          {error && (
            <p role="alert" className="text-red-700 font-semibold">
              {error}
            </p>
          )}

          {!loading && !error && (
            <div className="overflow-hidden rounded-xl border border-glass-border">
              <div className="w-full overflow-x-auto">
                <table className="min-w-[760px] w-full border-collapse text-left">
                  <thead className="bg-bg text-text-light text-sm">
                    <tr className="text-center">
                      <th scope="col" className="p-4">
                        Sr. No.
                      </th>
                      <th scope="col" className="p-4">
                        Email
                      </th>
                      <th scope="col" className="p-4">
                        Created Date
                      </th>
                      <th scope="col" className="p-4">
                        Type
                      </th>
                      <th scope="col" className="p-4">
                        Status
                      </th>
                      <th scope="col" className="p-4">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody className="text-center">
                    {users.map((user, index) => {
                      const rowKey = `${user.session_id}-${user.interview_no || 'na'}-${user.email || index}`;
                      const isDeleting = deleteLoadingId === rowKey;

                      return (
                        <tr
                          key={rowKey}
                          className="border-t border-glass-border hover:bg-muted focus-within:bg-muted text-text-light"
                        >
                          <td className="p-4">{index + 1}</td>
                          <td className="p-4 break-all">{user.email}</td>
                          <td className="p-4">{new Date(user.created_at).toLocaleString()}</td>

                          <td className="p-4 break-all">{user.interview_type}</td>

                          <td className="p-4">
                            <span
                              style={
                                theme === 'dark'
                                  ? { backgroundColor: '#FFD700', color: '#000000' }
                                  : {}
                              }
                              className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                                user.is_completed
                                  ? theme === 'dark'
                                    ? ''
                                    : 'bg-green-700 text-white'
                                  : 'bg-yellow-600 text-white'
                              }`}
                            >
                              {user.is_completed ? 'Completed' : 'Pending'}
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="relative group">
                                <button
                                  onClick={() => fetchChatHistory(user, 'transcript')}
                                  aria-label={`View transcript for ${user.email}`}
                                  title="Transcript"
                                  className="inline-flex items-center justify-center p-2 rounded-md border border-glass-border hover:bg-muted focus:outline-none focus:ring-2 focus:ring-accent"
                                >
                                  <FileText size={22} />
                                </button>
                                <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 rounded bg-bg px-2 py-1 text-xs text-text-light opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 whitespace-nowrap">
                                  Transcript
                                </span>
                              </div>
                              <div className="relative group">
                                <button
                                  onClick={() => fetchChatHistory(user, 'persona')}
                                  aria-label={`View persona for ${user.email}`}
                                  title="Persona"
                                  className="inline-flex items-center justify-center p-2 rounded-md border border-glass-border hover:bg-muted focus:outline-none focus:ring-2 focus:ring-accent"
                                >
                                  <UserRound size={18} />
                                </button>
                                <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 rounded bg-bg px-2 py-1 text-xs text-text-light opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 whitespace-nowrap">
                                  Persona
                                </span>
                              </div>
                              <div className="relative group">
                                <button
                                  onClick={() => fetchChatHistory(user, 'courses')}
                                  aria-label={`View recommended courses for ${user.email}`}
                                  title="Recommended Courses"
                                  className="inline-flex items-center justify-center p-2 rounded-md border border-glass-border hover:bg-muted focus:outline-none focus:ring-2 focus:ring-accent"
                                >
                                  <BookOpen size={18} />
                                </button>
                                <span className="pointer-events-none absolute -top-8 left-1/4 -translate-x-1/2 rounded bg-bg px-2 py-1 text-xs text-text-light opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 whitespace-nowrap">
                                  Recommended Courses
                                </span>
                              </div>
                              <div className="relative group">
                                <button
                                  onClick={() => deleteUser(user)}
                                  aria-label={`Delete user ${user.email}`}
                                  title="Delete User"
                                  disabled={isDeleting}
                                  className="inline-flex items-center justify-center p-2 rounded-md border border-glass-border hover:bg-muted focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <Trash2 size={18} />
                                </button>
                                <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 rounded bg-bg px-2 py-1 text-xs text-text-light opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 whitespace-nowrap">
                                  {isDeleting ? 'Deleting...' : 'Delete User'}
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Inline Review Panel */}
          {selectedSession && (
            <div
              className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50"
              role="dialog"
              aria-modal="true"
              aria-labelledby="chat-title"
            >
              <div className="bg-panel w-full max-w-6xl h-[90vh] rounded-xl shadow-xl border border-glass-border flex flex-col">
                <div className="flex justify-between items-center border-b p-4">
                  <h2 id="chat-title" className="text-lg font-bold text-text-light truncate">
                    {modalTitleByView[modalView] || `Review - ${selectedSession}`}
                  </h2>

                  <button
                    onClick={closeModal}
                    aria-label="Close chat history"
                    className="p-2 border border-glass-border rounded-md hover:bg-muted focus:ring-2 focus:ring-accent"
                  >
                    <X size={22} />
                  </button>
                </div>

                <div className="max-h-[78vh] overflow-y-auto p-6 space-y-6">
                  {chatLoading && <Loader />}

                  {!chatLoading && modalView === 'transcript' && chatData.length === 0 && (
                    <p className="text-text-muted text-center">No chat history found.</p>
                  )}

                  {!chatLoading && modalView === 'transcript' && (
                    <ol className="space-y-4">
                      {chatData.map((chat, index) => (
                        <li
                          key={index}
                          className="list-none rounded-lg border border-glass-border bg-panel-bg p-4 space-y-4"
                        >
                          <div className="flex items-start gap-3">
                            <Bot size={24} aria-hidden="true" />
                            <div className="w-full bg-muted p-4 rounded-lg text-sm text-left">
                              <strong>Q{index + 1}:</strong> {chat.question}
                            </div>
                          </div>
                          <div className="flex items-start gap-3">
                            <User size={24} aria-hidden="true" />
                            <div className="w-full bg-bg text-text-light p-4 rounded-lg text-sm text-left">
                              <strong>A{index + 1}:</strong> {chat.answer}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}

                  {!chatLoading && modalView === 'persona' && (
                    <div className="border border-glass-border rounded-lg p-5 bg-panel-bg">
                      <h3 className="text-lg font-bold mb-3">AI Generated Persona</h3>

                      <div className="text-sm leading-relaxed whitespace-pre-wrap">
                        {persona || 'Persona not available.'}
                      </div>
                      <div className="text-sm leading-relaxed whitespace-pre-wrap">
                        {personaInformation}
                      </div>
                    </div>
                  )}

                  {!chatLoading && modalView === 'courses' && (
                    <div>
                      {/* <h3 className="text-lg font-bold mb-4">
                        Courses Pathway
                      </h3> */}

                      {courses.length === 0 && (
                        <p className="text-text-muted text-center">No recommended courses found.</p>
                      )}

                      <div className="space-y-3">
                        {courses.map((course, index) => (
                          <div
                            key={index}
                            className="bg-panel-bg border border-glass-border rounded-lg p-4 text-sm"
                          >
                            <p className="font-semibold">{course.title}</p>
                            <div className="mt-3 flex gap-3 flex-wrap">
                              {getCourseLink(course, 'video') && (
                                <a
                                  href={getCourseLink(course, 'video')}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="underline text-teal-accent"
                                >
                                  Open Video
                                </a>
                              )}
                              {getCourseLink(course, 'pdf') && (
                                <a
                                  href={getCourseLink(course, 'pdf')}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="underline text-yellow-accent"
                                >
                                  Open PDF
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </section>
      </main>
    </>
  );
}
